import React, { useState, useEffect } from 'react';
import { FormGroup, Form } from 'semantic';
import { useTranslation } from 'react-i18next';
import CardStyleRadioButton from 'components/form-fields/CardStyleRadioButton';
import { ChargingGroup } from 'subdomains/cpo/types/charging-policy';
import { ChargingGroupPreview } from 'subdomains/cpo/screens/policies-and-groups/screens/provider-admin/charging-groups/ChargingGroupPreview';
import { SearchDropdown } from 'components';
import { request } from 'utils/api';
import { ChargingGroupModal } from 'subdomains/cpo/screens/policies-and-groups/screens/provider-admin/charging-groups/form/ChargingGroupModal';
import { Button } from 'semantic-ui-react';
import ConfirmDelete from 'components/ConfirmDelete';
import { CHARGING_GROUPS_V1_API_URL } from 'subdomains/cpo/router/api';
import { useMutation } from '@tanstack/react-query';

type Props = {
  values?: string[];
  onChange?: (values: string[]) => void;
};

const ChargingPolicyAssociationSelection: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [isAllSelected, setIsAllSelected] = useState(!props.values?.length);
  const [selectedChargingGroups, setSelectedChargingGroups] = useState<
    ChargingGroup[]
  >([]);

  useEffect(() => {
    if (isAllSelected) {
      props.onChange?.([]);
    } else if (selectedChargingGroups.length > 0) {
      props.onChange?.(selectedChargingGroups.map((g) => g.id));
    }
  }, [isAllSelected, selectedChargingGroups]);

  const fetchChargingGroups = async (body: Record<string, unknown>) => {
    const { data } = await request<{ data: ChargingGroup[] }>({
      path: '/1/charging-groups/search',
      method: 'POST',
      body,
    });
    return data;
  };

  const refetchChargingGroups = async () => {
    if (props.values && props.values.length > 0) {
      const data = await fetchChargingGroups({ ids: props.values });
      setSelectedChargingGroups(
        data.filter((g) => props.values?.includes(g.id))
      );
    } else {
      setSelectedChargingGroups([]);
    }
  };

  useEffect(() => {
    refetchChargingGroups();
  }, []);

  const handleModeChange = (isAll: boolean) => {
    if (isAll !== isAllSelected) {
      setSelectedChargingGroups([]);
    }
    setIsAllSelected(isAll);
  };

  const deleteChargingGroupMutation = useMutation({
    mutationFn: async (id: string) => {
      await request({
        path: `${CHARGING_GROUPS_V1_API_URL}/${id}`,
        method: 'DELETE',
      });
    },
    onSuccess: refetchChargingGroups,
  });

  return (
    <>
      <FormGroup style={{ marginLeft: '0', marginRight: '0' }}>
        <CardStyleRadioButton
          isSelected={isAllSelected}
          onClick={() => handleModeChange(true)}
          text={t(
            'providerAdmin.chargingPolicyForm.associationSelection.allChargingStationsButtonText',
            'All Charging Stations'
          )}
        />
        <CardStyleRadioButton
          isSelected={!isAllSelected}
          onClick={() => handleModeChange(false)}
          text={t(
            'providerAdmin.chargingPolicyForm.associationSelection.chargingGroupsButtonText',
            'Charging Groups'
          )}
        />
      </FormGroup>
      {!isAllSelected && (
        <Form.Field required>
          <label>
            {t(
              'providerAdmin.chargingPolicyForm.associationSelection.selectChargingGroupsLabel',
              'Select Charging Groups'
            )}
          </label>
          <SearchDropdown
            value={selectedChargingGroups}
            multiple
            objectMode
            getOptionLabel={(item: ChargingGroup) => item.label}
            getOptionValue={(item: ChargingGroup) => item.id}
            onChange={(
              _: React.ChangeEvent<HTMLInputElement>,
              { value }: { value: ChargingGroup[] }
            ) => {
              setSelectedChargingGroups(value);
              props.onChange?.(value.map((g) => g.id));
            }}
            onDataNeeded={fetchChargingGroups}
          />
        </Form.Field>
      )}
      {selectedChargingGroups.map((group) => (
        <ChargingGroupPreview
          label={group.label}
          numConnectors={group.filters?.reduce((numConnectors, filter) => {
            if (filter.connectors?.length) {
              numConnectors += filter.connectors.length;
            }
            return numConnectors;
          }, 0)}
          key={group.id}
          actionComponents={
            <>
              <ChargingGroupModal
                trigger={
                  <Button
                    basic
                    icon="pen-to-square"
                    title={t('common.edit', 'Edit')}
                    onClick={(e) => e.preventDefault()}
                  />
                }
                onSuccess={refetchChargingGroups}
                chargingGroup={group}
                mode="edit"
              />
              <ConfirmDelete
                onConfirm={async () => {
                  // proactively remove the group from the selected list.
                  // we don't necessarily need to do this, because after the mutation, we'll refetch the list.
                  // but this just makes it a bit snappier, especially on slow connections
                  setSelectedChargingGroups(
                    selectedChargingGroups.filter((g) => g.id !== group.id)
                  );
                  deleteChargingGroupMutation.mutate(group.id);
                }}
                name={group.label}
                objectName={t('common.chargingGroup', 'Charging Group')}
                trigger={
                  <Button
                    basic
                    icon="trash"
                    title={t('common.delete', 'Delete')}
                    onClick={(e) => e.preventDefault()}
                  />
                }
              />
            </>
          }
        />
      ))}
    </>
  );
};

export default ChargingPolicyAssociationSelection;
